'use strict';

var _ = require('lodash');
var CalculatedColumnService = function CalculatedColumnService(formulaEvalService) {
  var dependentCalColByFieldMap = {};

  /**
   * calculates formula based columns and adds it to a data model
   * @param columns
   *          columns from gridSettings.columns
   * @param dataFields
   *          dataField configuration
   * @param data
   *          model of data that will be changed
   */
  function calculateAndAddColumnsToModel(columns, dataFields, data) {
    var dependencyModel = prepareTopologicalAlgColumnsDependencyModel(columns);
    var sortedDependentColumnsFields = topologicalSortAlgorithm(dependencyModel);
    var sortedDependentColumns = getColumnsFromColumnFieldMap(sortedDependentColumnsFields);
    calculateFormulaBasedColumns(sortedDependentColumns, dataFields, data);
  }

  /**
   * creates columns dependency model used when recalculating custom columns
   * @param columns
   *          all data columns (ex. from $scope.gridSettings.columns)
   * @returns dependencyModel {Array}
   *          dependency model where any object in an array contains columnsBasedOn: columns that has to be recalculated when columnField change
   */
  function prepareCellEditColumnsDependencyModel(columns) {
    var cellEditDependencyModel = [];
    columns.forEach(function (col) {
      getColumnsBasedOnThisColumn(columns, col, cellEditDependencyModel);
    });
    return cellEditDependencyModel;
  }

  /**
   * recalculates and changes all values in a row that dependent on changed cell and cell itself
   * @param rowData
   *          row where changed cell is
   * @param dependencyModel
   *          information about every column that was based on changed column
   * @param columnDataField
   *          changed column field
   * @param changedColumnNames
   *          array where changed column names will be saved
   */
  function changeAllDependentData(rowData, dependencyModel, columnDataField, changedColumnNames) {
    dependencyModel.forEach(function (element) {
      if (!_.isEmpty(element.columnsBasedOn) && element.columnField === columnDataField) {
        element.columnsBasedOn.forEach(function (colBasedOn) {
          if (changedColumnNames) {
            changedColumnNames.push(colBasedOn.field);
          }
          rowData[colBasedOn.field] = formulaEvalService.compute(colBasedOn.formula, rowData);
          changeAllDependentData(rowData, dependencyModel, colBasedOn.field, changedColumnNames);
        });
      }
    });
  }
  function prepareTopologicalAlgColumnsDependencyModel(columns) {
    var dependencyModel = [];
    columns.forEach(function (column) {
      if (isGroupedColumn(column)) {
        column.columns.forEach(function (groupedCol) {
          if (isCalculatedColumn(groupedCol)) {
            dependentCalColByFieldMap[groupedCol.field] = groupedCol;
          }
        });
      } else {
        if (isCalculatedColumn(column)) {
          dependentCalColByFieldMap[column.field] = column;
        }
      }
    });
    function addDependentColumnToNode(node, column, operand) {
      var value = column.formula[operand];
      if (dependentCalColByFieldMap[value]) {
        node.baseColumns.push(dependentCalColByFieldMap[value]);
      }
    }
    function getDependency(column) {
      var node = {};
      node.baseColumns = [];
      node.columnField = column.field;
      addDependentColumnToNode(node, column, 'rightOperand');
      addDependentColumnToNode(node, column, 'leftOperand');
      return node;
    }
    function getDependencyForGroupColumn(columnGroup) {
      var node = [];
      columnGroup.columns.forEach(function (groupedCol) {
        if (isCalculatedColumn(groupedCol)) {
          var rightOperandGroupValue = groupedCol.formula.rightOperand;
          var leftOperandGroupValue = groupedCol.formula.leftOperand;
          var arrayElementObj = {
            baseColumns: [],
            columnField: groupedCol.field
          };
          if (dependentCalColByFieldMap[rightOperandGroupValue]) {
            arrayElementObj.baseColumns.push(dependentCalColByFieldMap[rightOperandGroupValue]);
          }
          if (dependentCalColByFieldMap[leftOperandGroupValue]) {
            arrayElementObj.baseColumns.push(dependentCalColByFieldMap[leftOperandGroupValue]);
          }
          node.push(arrayElementObj);
        }
      });
      return node;
    }
    columns.forEach(function (column) {
      if (isCalculatedColumn(column)) {
        dependencyModel.push(getDependency(column));
      } else if (isGroupedColumn(column)) {
        var dependentColumnsFromGroup = getDependencyForGroupColumn(column);
        dependentColumnsFromGroup.forEach(function (depColFromGroup) {
          dependencyModel.push(depColFromGroup);
        });
      }
    });
    return dependencyModel;
  }
  function topologicalSortAlgorithm(dependencyModel) {
    var result = [];
    var initialModelLength = dependencyModel.length;
    function removeAllColumnDependency(columnToRemoveField) {
      dependencyModel.forEach(function (node) {
        for (var i = 0; i < node.baseColumns.length; i++) {
          if (node.baseColumns[i].field === columnToRemoveField) {
            node.baseColumns.splice(i, 1);
          }
        }
      });
    }
    function addToResult(adjacentColumn) {
      result.push(adjacentColumn);
    }
    for (var j = 0; j < initialModelLength; j++) {
      for (var i = 0; i < dependencyModel.length; i++) {
        if (_.isEmpty(dependencyModel[i].baseColumns)) {
          removeAllColumnDependency(dependencyModel[i].columnField);
          addToResult(dependencyModel[i].columnField);
          dependencyModel.splice(i, 1);
          break;
        }
      }
    }
    return result;
  }
  function getColumnsFromColumnFieldMap(sortedDependentColumnsFields) {
    var sortedDependentColumns = [];
    sortedDependentColumnsFields.forEach(function (field) {
      var value = dependentCalColByFieldMap[field];
      sortedDependentColumns.push(value);
    });
    return sortedDependentColumns;
  }
  function calculateFormulaBasedColumns(columns, dataFields, data) {
    columns.forEach(function (column) {
      calculate(column, dataFields, data);
    });
  }
  function calculate(column, dataFields, data) {
    calculateValue(data, column);
    completeDataFields(column, dataFields);
  }
  function calculateValue(data, column) {
    data.forEach(function (rowData) {
      var columnField = column.field;
      rowData[columnField] = formulaEvalService.compute(column.formula, rowData);
    });
  }
  function completeDataFields(column, dataFields) {
    dataFields.push({
      name: column.field,
      type: 'number'
    });
  }
  function isGroupedColumn(column) {
    return !_.isEmpty(column.columns);
  }
  function isCalculatedColumn(column) {
    return !_.isUndefined(column.formula);
  }

  /**
   * creates one array element of cellEditDependencyModel and push it to it
   * @param columns
   *          all data columns (ex. from $scope.gridSettings.columns)
   * @param column
   *          column that potentially will cause another column recalculation when change . When it's group of columns, all column from that group will be examined
   * @param cellEditDependencyModel
   */
  function getColumnsBasedOnThisColumn(columns, column, cellEditDependencyModel) {
    var columnsBasedOnThisArr = [];

    /**
     * creates array element of cellEditDependencyModel
     * @param possibleDependent
     *          column that will be recalculated when 'columnField' changes
     * @param column
     *          column that is examined to have any dependent column
     */
    function addToColumnBasedOnThisArray(possibleDependent, column) {
      var columnsBasedOnThis = {
        columnField: {},
        columnsBasedOn: []
      };
      if (isCalculatedColumn(possibleDependent) === false) {
        return;
      }
      if (possibleDependent.formula.leftOperand === column.field || possibleDependent.formula.rightOperand === column.field) {
        columnsBasedOnThis.columnField = column.field;
        columnsBasedOnThis.columnsBasedOn.push(possibleDependent);
        columnsBasedOnThisArr.push(columnsBasedOnThis);
      }
    }
    columns.forEach(function (possibleDependent) {
      if (isCalculatedColumn(possibleDependent)) {
        if (isGroupedColumn(column)) {
          column.columns.forEach(function (groupedCol) {
            addToColumnBasedOnThisArray(possibleDependent, groupedCol);
          });
        } else {
          addToColumnBasedOnThisArray(possibleDependent, column);
        }
      } else if (!_.isEmpty(possibleDependent.columns)) {
        possibleDependent.columns.forEach(function (possibleDependentGroup) {
          if (isGroupedColumn(column)) {
            column.columns.forEach(function (groupedCol) {
              addToColumnBasedOnThisArray(possibleDependentGroup, groupedCol);
            });
          } else {
            addToColumnBasedOnThisArray(possibleDependentGroup, column);
          }
        });
      }
    });
    for (var i = 0; i < columnsBasedOnThisArr.length; i++) {
      cellEditDependencyModel.push(columnsBasedOnThisArr[i]);
    }
  }
  return {
    calculateAndAddColumnsToModel: calculateAndAddColumnsToModel,
    prepareCellEditColumnsDependencyModel: prepareCellEditColumnsDependencyModel,
    changeAllDependentData: changeAllDependentData
  };
};
CalculatedColumnService.$inject = ['FormulaEvalService'];
module.exports = CalculatedColumnService;